/**
 * http.service.ts — Layer 1: HTTP Transport
 *
 * The ONLY file in the app that calls `httpClient` directly.
 * Provides typed, verb-named helpers so the rest of the service
 * layer never has to touch raw fetch/httpClient options.
 *
 * ❌ Never import this file from a React component or hook.
 *    Import from a domain service (e.g. auth.service.ts) instead.
 */

import httpClient, { HttpClientSettings } from '../functions/httpClient';

// ---------------------------------------------------------------------------
// Standard CRUD verbs
// ---------------------------------------------------------------------------

/** GET — returns parsed response body */
export const getRequest = <T = unknown>(
  url: string,
  signal?: AbortSignal,
): Promise<T> =>
  httpClient(url, { method: 'GET', ...(signal ? { signal } : {}) }) as Promise<T>;

/** POST with a JSON body. `opts` forwards extra fetch options (e.g. { keepalive: true } for a flush-on-unload save). */
export const postRequest = <T = unknown>(
  url: string,
  body?: Record<string, unknown> | unknown[],
  opts?: Pick<HttpClientSettings, 'keepalive' | 'signal'>,
): Promise<T> =>
  httpClient(url, { method: 'POST', body, ...opts }) as Promise<T>;

/** PUT with a JSON body */
export const putRequest = <T = unknown>(
  url: string,
  body?: Record<string, unknown> | unknown[],
): Promise<T> =>
  httpClient(url, { method: 'PUT', body }) as Promise<T>;

/** PATCH with a JSON body */
export const patchRequest = <T = unknown>(
  url: string,
  body?: Record<string, unknown> | unknown[],
): Promise<T> =>
  httpClient(url, { method: 'PATCH', body }) as Promise<T>;

/** DELETE (no body) */
export const deleteRequest = <T = unknown>(url: string): Promise<T> =>
  httpClient(url, { method: 'DELETE' }) as Promise<T>;

/** DELETE with a JSON body (for endpoints that read req.body for the identifier) */
export const deleteWithBodyRequest = <T = unknown>(
  url: string,
  body: Record<string, unknown>,
): Promise<T> =>
  httpClient(url, { method: 'DELETE', body }) as Promise<T>;

// ---------------------------------------------------------------------------
// Specialised variants
// ---------------------------------------------------------------------------

/** POST with a FormData body (Content-Type is set automatically by the browser) */
export const postFormData = <T = unknown>(
  url: string,
  formData: FormData,
): Promise<T> =>
  httpClient(url, { method: 'POST', body: formData }) as Promise<T>;

/** PUT with a FormData body */
export const putFormData = <T = unknown>(
  url: string,
  formData: FormData,
): Promise<T> =>
  httpClient(url, { method: 'PUT', body: formData }) as Promise<T>;

/**
 * GET — returns the raw `Response` object instead of parsed data.
 * Use when you need status codes, headers, or streaming (e.g. file downloads).
 */
export const getRawResponse = (url: string): Promise<Response> =>
  httpClient(url, { method: 'GET', onlyResponse: true }) as Promise<Response>;

/** POST and return raw Response (e.g. for checking Content-Disposition headers) */
export const postRawResponse = (
  url: string,
  body?: Record<string, unknown>,
): Promise<Response> =>
  httpClient(url, {
    method: 'POST',
    body,
    onlyResponse: true,
  }) as Promise<Response>;
